/**
 * useTouchpoint — the 26747 touchpoint emitter shared by ora and the Outlook
 * add-in.
 *
 * `TouchpointProvider` buffers events client-side and flushes them:
 *   - immediately once the buffer reaches 10 events,
 *   - after 5s of no new events ("idle"),
 *   - the moment the tab becomes hidden (`visibilitychange`), and
 *   - on `pagehide` (navigation away / tab close) — the two events a
 *     touchpoint pipeline cannot afford to lose, since there is no "next
 *     render" to flush on.
 *
 * Delivery prefers `navigator.sendBeacon` (survives page unload, which a
 * plain `fetch` does not); `fetch(..., { keepalive: true })` is the fallback
 * when `sendBeacon` is unavailable or refuses the payload. Neither path is
 * ever awaited — a slow or failing telemetry POST must not be able to delay
 * or break anything the caller is doing. `emit` and `flush` both swallow
 * every error for the same reason (mirrors `drawerTelemetry.ts`'s "telemetry
 * must never break a render" rule).
 *
 * `sink` is a host/test seam: supply it to intercept the flushed batch
 * synchronously (no network) — the default (`undefined`) behavior is the
 * real `sendBeacon`/`fetch` delivery described above.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import type {
  TouchpointEvent,
  TouchpointKind,
  TouchpointPhase,
  TouchpointSubjectType,
  TouchpointSurface,
} from "./touchpoints";

const MAX_BUFFER = 10;
const IDLE_FLUSH_MS = 5000;
const MAX_IDENTIFIER_LENGTH = 128;

export type TouchpointSink = (events: TouchpointEvent[]) => void;

// ---------------------------------------------------------------------------
// Module-level bridge (P2.11) — an escape hatch for callers OUTSIDE React,
// or call sites that pre-date this provider and cannot be rewritten to
// `useTouchpoint()` without breaking pinned tests that mock their own
// module (e.g. the Outlook add-in's `obs/touchpoints.ts`). Points at
// whichever `TouchpointProvider` is currently mounted; `null` (a safe
// no-op for the caller) when none is — mirrors `useTouchpoint()`'s own
// outside-a-provider behavior.
// ---------------------------------------------------------------------------
let activeEmit: TouchpointContextValue["emit"] | null = null;

/** Returns the mounted `TouchpointProvider`'s `emit`, or `null` when none is
 *  mounted. Prefer `useTouchpoint()` inside React; this exists only for
 *  non-hook bridges. */
export function getActiveTouchpointEmit(): TouchpointContextValue["emit"] | null {
  return activeEmit;
}

export interface EmitFields {
  subject_type?: TouchpointSubjectType;
  subject_id?: string;
  surface?: TouchpointSurface;
  mailbox_key?: string;
  client_version?: string;
}

export interface TouchpointContextValue {
  emit: (kind: TouchpointKind, phase: TouchpointPhase, fields?: EmitFields) => void;
}

const NOOP_CONTEXT: TouchpointContextValue = { emit: () => {} };

const TouchpointContext = createContext<TouchpointContextValue | null>(null);

function isDevMode(): boolean {
  try {
    // Read via `globalThis` (cast, not the bare `process` identifier): this
    // file is shared into the Outlook add-in's TS program via P2.11's wiring
    // (`main.tsx` / `obs/touchpoints.ts`), and the add-in's tsconfig carries
    // no `@types/node`, so `process` is not an ambiently-typed name there —
    // ora's own tsconfig has it, which is why this only surfaced once the
    // add-in started reaching this module too.
    const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } })
      .process;
    return typeof proc !== "undefined" && proc?.env?.NODE_ENV !== "production";
  } catch {
    return false;
  }
}

/**
 * Dev-mode-only guard: touchpoints carry IDENTIFIERS ONLY. Returns `false`
 * (and logs, never throws) when a string field other than `mailbox_key`
 * contains `@`, or any string field exceeds 128 chars — the caller drops the
 * event rather than post it. A no-op (always `true`) outside dev mode, so
 * production never pays for the scan.
 */
function passesIdentifierShape(fields: Record<string, unknown>): boolean {
  if (!isDevMode()) return true;
  let ok = true;
  for (const [key, value] of Object.entries(fields)) {
    if (typeof value !== "string") continue;
    if (key !== "mailbox_key" && value.includes("@")) {
      // eslint-disable-next-line no-console
      console.error(
        `[touchpoints] dev-mode: field "${key}" looks like an email address — touchpoints carry identifiers only, dropping the event`,
      );
      ok = false;
    }
    if (value.length > MAX_IDENTIFIER_LENGTH) {
      // eslint-disable-next-line no-console
      console.error(
        `[touchpoints] dev-mode: field "${key}" is longer than ${MAX_IDENTIFIER_LENGTH} chars — touchpoints carry identifiers only, dropping the event`,
      );
      ok = false;
    }
  }
  return ok;
}

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto fallback below.
  }
  return `tp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Real delivery: sendBeacon first (survives unload), fetch keepalive as the
 *  fallback. Never awaited by the caller — any rejection is caught here so it
 *  can never surface as an unhandled promise rejection either. */
function deliverOverNetwork(postUrl: string, events: TouchpointEvent[]): void {
  if (events.length === 0) return;
  try {
    const body = JSON.stringify({ events });
    let beaconSent = false;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([body], { type: "application/json" });
        beaconSent = navigator.sendBeacon(postUrl, blob);
      } catch {
        beaconSent = false;
      }
    }
    if (!beaconSent && typeof fetch === "function") {
      void fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // Never throws / never surfaces — telemetry must never break the page.
      });
    }
  } catch {
    // Telemetry must never break the page. Deliberately silent.
  }
}

export interface TouchpointProviderProps {
  /** `POST /api/workflows/telemetry/touchpoints` (or the add-in's equivalent). */
  postUrl: string;
  /** Test/host seam — see the module doc. Defaults to real network delivery. */
  sink?: TouchpointSink;
  /** Default surface for every event this provider emits — set once per app
   *  root (ora root = "ora_web", the add-in's App.tsx = "outlook_addin").
   *  Overridable per call via `emit`'s `fields.surface`. */
  surface?: TouchpointSurface;
  /** Optional provider-level default subject — a page that only ever emits
   *  about ONE run/brief can set this once instead of repeating it on every
   *  `emit` call. Per-call `fields.subject_id`/`subject_type` still win. */
  subjectId?: string;
  subjectType?: TouchpointSubjectType;
  mailboxKey?: string;
  clientVersion?: string;
  children: ReactNode;
}

export function TouchpointProvider({
  postUrl,
  sink,
  surface = "ora_web",
  subjectId,
  subjectType = "run",
  mailboxKey,
  clientVersion,
  children,
}: TouchpointProviderProps) {
  // One session_key per PROVIDER MOUNT, not per emit — generated lazily on
  // first render so it survives StrictMode's double-invoke without changing.
  const sessionKeyRef = useRef<string | null>(null);
  if (sessionKeyRef.current == null) {
    sessionKeyRef.current = newId();
  }

  const bufferRef = useRef<TouchpointEvent[]>([]);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const deliver = useCallback(
    (events: TouchpointEvent[]) => {
      if (events.length === 0) return;
      if (sink) {
        try {
          sink(events);
        } catch {
          // A failing sink must never throw back into the caller.
        }
        return;
      }
      deliverOverNetwork(postUrl, events);
    },
    [postUrl, sink],
  );

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    clearIdleTimer();
    if (bufferRef.current.length === 0) return;
    const events = bufferRef.current;
    bufferRef.current = [];
    deliver(events);
  }, [clearIdleTimer, deliver]);

  const scheduleIdleFlush = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      flush();
    }, IDLE_FLUSH_MS);
  }, [clearIdleTimer, flush]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    const onPagehide = () => flush();
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onPagehide);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", onPagehide);
      }
      clearIdleTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flush]);

  const emit = useCallback<TouchpointContextValue["emit"]>(
    (kind, phase, fields) => {
      try {
        const resolvedSubjectId = fields?.subject_id ?? subjectId;
        if (!resolvedSubjectId) return; // nothing to attach this event to.

        const resolvedMailboxKey = fields?.mailbox_key ?? mailboxKey;
        const resolvedClientVersion = fields?.client_version ?? clientVersion;

        if (
          !passesIdentifierShape({
            subject_id: resolvedSubjectId,
            mailbox_key: resolvedMailboxKey,
            client_version: resolvedClientVersion,
            session_key: sessionKeyRef.current,
          })
        ) {
          return;
        }

        const event: TouchpointEvent = {
          event_key: newId(),
          kind,
          phase,
          subject_type: fields?.subject_type ?? subjectType,
          subject_id: resolvedSubjectId,
          surface: fields?.surface ?? surface,
          occurred_at: new Date().toISOString(),
          session_key: sessionKeyRef.current ?? undefined,
          ...(resolvedMailboxKey ? { mailbox_key: resolvedMailboxKey } : {}),
          ...(resolvedClientVersion ? { client_version: resolvedClientVersion } : {}),
        };

        bufferRef.current.push(event);
        if (bufferRef.current.length >= MAX_BUFFER) {
          flush();
        } else {
          scheduleIdleFlush();
        }
      } catch {
        // emit must never throw.
      }
    },
    [subjectId, subjectType, surface, mailboxKey, clientVersion, flush, scheduleIdleFlush],
  );

  const value = useMemo<TouchpointContextValue>(() => ({ emit }), [emit]);

  // Register with the module-level bridge (see above) so a non-hook caller
  // — e.g. the add-in's `obs/touchpoints.ts` — can reach this provider's
  // `emit` without a prop-drilled reference. Last-mounted-wins; the cleanup
  // only clears the slot if THIS provider still owns it, so an outer
  // provider unmounting after an inner one has already taken over never
  // clobbers the inner one's registration.
  useEffect(() => {
    activeEmit = emit;
    return () => {
      if (activeEmit === emit) activeEmit = null;
    };
  }, [emit]);

  return (
    <TouchpointContext.Provider value={value}>
      {children}
    </TouchpointContext.Provider>
  );
}

/**
 * Returns the nearest `TouchpointProvider`'s `emit`, or a safe no-op when
 * rendered outside one — a page that emits touchpoints must not crash (or
 * require a provider-presence check at every call site) just because the
 * app root hasn't mounted the real provider yet.
 */
export function useTouchpoint(): TouchpointContextValue {
  return useContext(TouchpointContext) ?? NOOP_CONTEXT;
}

export default TouchpointProvider;
